import { flattenStack, stackCatalogues } from './catalogue';
import { formatMessage } from './message/format';
import { dirOf } from './resolve';
import type {
  Catalogue,
  CatalogueSet,
  Locale,
  MessageArgs,
  MessageRef,
  TextDirection,
  UseCase,
} from './types';

export interface Translator<K extends string = string> {
  readonly locale: Locale;
  readonly useCase: UseCase;
  readonly dir: TextDirection;
  readonly timeZone: string | undefined;
  /**
   * The message for `key` formatted with `args`. A key nothing defines renders
   * `fallback` when one is given, otherwise the key itself, and is reported once.
   */
  translate(key: K, args?: MessageArgs, fallback?: string): string;
  /** `translate` with a fallback and no arguments. */
  translate(key: K, fallback: string): string;
  has(key: string): boolean;
  /** Renders a `MessageRef` that arrived over the wire. */
  ref(ref: MessageRef): string;
}

export interface TranslatorOptions {
  readonly locale: Locale;
  readonly useCase: UseCase;
  readonly catalogues: CatalogueSet;
  readonly timeZone?: string | undefined;
  /** Called once per missing key; defaults to one `console.warn` per key. */
  readonly onMissing?: ((key: string, locale: Locale) => void) | undefined;
}

const TERM_PREFIX = 'term.';

/** `term.record` becomes the argument `term_record`, available to every message. */
export function termArgs(messages: ReadonlyMap<string, string>): MessageArgs {
  const entries: Array<[string, string]> = [];
  for (const [key, value] of messages) {
    if (key.startsWith(TERM_PREFIX))
      entries.push([`term_${key.slice(TERM_PREFIX.length)}`, value]);
  }
  return Object.fromEntries(entries);
}

export function createTranslator<K extends string = string>(
  options: TranslatorOptions,
): Translator<K> {
  const { locale, useCase, timeZone } = options;
  const messages = flattenStack(
    stackCatalogues(options.catalogues, locale, useCase),
  );
  const terms = termArgs(messages);
  const reported = new Set<string>();
  const onMissing =
    options.onMissing ??
    ((key: string, at: Locale) => {
      console.warn(`[i18n] no message for "${key}" in ${at}`);
    });
  const format = { locale, timeZone };

  const translate = (
    key: string,
    argsOrFallback?: MessageArgs | string,
    fallback?: string,
  ): string => {
    const args =
      typeof argsOrFallback === 'string' ? undefined : argsOrFallback;
    const missingText =
      typeof argsOrFallback === 'string' ? argsOrFallback : fallback;
    const source = messages.get(key);
    if (source === undefined) {
      if (!reported.has(key)) {
        reported.add(key);
        onMissing(key, locale);
      }
      return missingText ?? key;
    }
    return formatMessage(source, args ? { ...terms, ...args } : terms, format);
  };

  return {
    locale,
    useCase,
    dir: dirOf(locale),
    timeZone,
    translate,
    has: (key) => messages.has(key),
    ref: (ref) => translate(ref.key, ref.params),
  };
}

/**
 * Ties a translator's key type to a base catalogue, so `t('ui.modal.close')`
 * typechecks and `t('ui.modal.clsoe')` does not. Overlays may only redefine
 * keys the base declares.
 */
export function defineCatalogue<const B extends Catalogue>(base: B) {
  type Key = keyof B & string;
  return {
    base,
    overlay(
      overlay: Partial<Record<Key, string>>,
    ): Partial<Record<Key, string>> {
      return overlay;
    },
    translator(options: TranslatorOptions): Translator<Key> {
      return createTranslator<Key>(options);
    },
  };
}
